import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { DeviceAccessTokens } from "@app/shared/src/api/api.gen";
import { Btn, Card, CardHead, Note, SettingsIcon, Spinner, Tag, useToast } from "@app/shared/src/components_v2";
import { cabCardUrl } from "../../helpers/cabLinks";
import { downloadCabCardPdf } from "../../helpers/cabCardPdf";
import { useLoad } from "../../hooks/useLoad";
import { useFleet } from "../providers/FleetProvider";
import { PageTitle } from "../shared/PageTitle";

/**
 * Every truck's cab card in one place — pick a unit, print the card, tape it in the cab.
 *
 * The QR encodes the revocable access token, never the device key. Issue/revoke (the rotation
 * story) lives with the unit under Device settings → Driver QR code; this page is the print shop.
 */
export const QrCodesPage: React.FC = () => {
    // Trucks, not every sensor in the org: a cab card on a traffic counter has no driver.
    const { statsDevices: devices, group, isLoading } = useFleet();
    const toast = useToast();
    const [selectedId, setSelectedId] = useState<string>();

    const tokens = useLoad(async () => {
        const entries = await Promise.all(
            devices.map(async (d) => {
                try {
                    const list = await DeviceAccessTokens.getForDevice(d.id);
                    const active = list?.find((t) => !t.revokedAt && (!t.expiresAt || new Date(t.expiresAt) > new Date()));
                    return [d.id!, active] as const;
                } catch {
                    return [d.id!, undefined] as const;
                }
            }),
        );
        return new Map(entries);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [devices.map((d) => d.id).join(",")]);

    const downloadCard = async (title: string, deviceKey: string | undefined, url: string) => {
        try {
            await downloadCabCardPdf({ title, deviceKey, url });
            toast("Cab card downloaded");
        } catch (err) {
            toast(err instanceof Error ? err.message : "Could not build the cab card");
        }
    };

    const issue = async (deviceId: string) => {
        try {
            await DeviceAccessTokens.issue(deviceId);
            toast("Cab card issued");
            tokens.reload();
        } catch (err) {
            toast(err instanceof Error ? err.message : "Could not issue a card");
        }
    };

    // Revoking from the print shop, not just from the unit's own settings: the moment you find out
    // a card has walked off is usually the moment you are looking at the sheet of them.
    const revoke = async (tokenId: string, deviceLabel: string) => {
        try {
            await DeviceAccessTokens.revoke(tokenId);
            toast(`${deviceLabel}'s cab card revoked`);
            tokens.reload();
        } catch (err) {
            toast(err instanceof Error ? err.message : "Could not revoke that card");
        }
    };

    const selected = useMemo(() => {
        const withCard = devices.filter((d) => tokens.data?.get(d.id!));
        return devices.find((d) => d.id === selectedId) ?? withCard[0] ?? devices[0];
    }, [devices, selectedId, tokens.data]);
    const selectedToken = selected ? tokens.data?.get(selected.id!) : undefined;
    const url = selectedToken ? cabCardUrl(selectedToken.token) : undefined;

    return (
        <div className="flex flex-col gap-4">
            <h1 className="text-[20px] font-bold tracking-tight">
                <PageTitle>Driver QR codes</PageTitle>
            </h1>

            <div className="grid items-start gap-4 lg:grid-cols-2">
                <Card className="print:border-0">
                    <CardHead title="Cab card" note={selected?.name} />
                    <div className="flex flex-col items-center gap-4 p-5">
                        {isLoading || tokens.isLoading ? (
                            <div className="grid h-[260px] place-items-center text-[var(--muted)]">
                                <Spinner />
                            </div>
                        ) : url && selected ? (
                            <>
                                {/* White backing regardless of theme: this block is what gets printed. */}
                                <div id="print-cab-card" className="flex flex-col items-center gap-2.5 rounded-[16px] bg-white p-6">
                                    <div className="text-[17px] font-bold text-[#0e141c]">{selected.name}</div>
                                    <QRCodeSVG value={url} size={180} marginSize={1} />
                                    <div className="font-mono text-[11px] tracking-wide text-[#5c6875]">{selected.deviceKey}</div>
                                    <div className="text-[10.5px] text-[#5c6875]">Scan to open this truck's cab screen — no login</div>
                                </div>
                                <div className="flex flex-wrap justify-center gap-2 print:hidden">
                                    <Btn onClick={() => downloadCard(selected.facilityName || selected.name || "Unit", selected.deviceKey, url)}>
                                        Download cab card (PDF)
                                    </Btn>
                                    <Btn tone="quiet" onClick={() => window.print()}>
                                        Print this page
                                    </Btn>
                                    <Btn tone="danger" onClick={() => revoke(selectedToken!.id!, selected.name ?? "That unit")}>
                                        Revoke
                                    </Btn>
                                </div>
                                <p className="max-w-[42ch] text-center text-[11.5px] leading-relaxed text-[var(--faint)] print:hidden">
                                    Revoking kills every printed copy and bookmark of this card immediately. Issue a new one to
                                    put the truck back in service.
                                </p>
                            </>
                        ) : (
                            <div className="flex flex-col items-center gap-3 py-8 text-center">
                                <p className="max-w-[38ch] text-[13px] text-[var(--muted)]">
                                    {selected
                                        ? `${selected.name} has no active cab card yet.`
                                        : group
                                          ? `No devices in ${group.name} yet.`
                                          : "No devices in this organization yet."}
                                </p>
                                {selected && (
                                    <Btn onClick={() => issue(selected.id!)} className="print:hidden">
                                        Issue a cab card
                                    </Btn>
                                )}
                            </div>
                        )}
                    </div>
                </Card>

                <Card className="print:hidden">
                    <CardHead title="Generate for a unit" />
                    <div className="mt-2 flex flex-col pb-2">
                        {/* Row, not <button>: the settings shortcut is a link, and a link inside a
                            button is invalid markup that browsers resolve unpredictably. */}
                        {devices.map((d) => {
                            const has = Boolean(tokens.data?.get(d.id!));
                            return (
                                <div
                                    key={d.id}
                                    className={`flex items-center gap-3 border-t border-[var(--line)] pr-3 first:border-t-0 hover:bg-[var(--surface-2)] ${
                                        selected?.id === d.id ? "bg-[var(--surface-2)]" : ""
                                    }`}
                                >
                                    <button
                                        type="button"
                                        onClick={() => setSelectedId(d.id)}
                                        className="flex min-w-0 flex-1 items-center gap-3 py-3 pl-5 text-left"
                                    >
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate text-[13.5px] font-semibold">{d.name}</span>
                                            <span className="block truncate font-mono text-[11.5px] text-[var(--muted)]">{d.deviceKey}</span>
                                        </span>
                                        {has ? <Tag tone="safe">Card active</Tag> : <Tag tone="mute">No card</Tag>}
                                    </button>
                                    <Link
                                        to={`/devices/${d.id}/settings`}
                                        title={`${d.name ?? "Unit"} settings`}
                                        aria-label={`Open settings for ${d.name ?? "this unit"}`}
                                        className="grid h-9 w-9 flex-none place-items-center rounded-[10px] text-[var(--faint)] hover:bg-[var(--surface-3)] hover:text-[var(--text)]"
                                    >
                                        <SettingsIcon size={17} />
                                    </Link>
                                </div>
                            );
                        })}
                    </div>
                    <div className="border-t border-[var(--line)] bg-[var(--surface-2)] px-5 py-3 text-[12px] leading-relaxed text-[var(--muted)]">
                        Scanning opens that truck only — no login. Reissuing or revoking a card lives with the unit under{" "}
                        <b className="font-semibold text-[var(--text)]">Device settings → Driver QR code</b>.
                    </div>
                </Card>
            </div>

            <Note className="print:hidden">
                Printing uses your browser's print dialog — the page strips itself down to just the card.
            </Note>
        </div>
    );
};
